import React, { memo, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { DispatcherSelect } from 'apps/fleet-planner/components/atoms'
import { DispatcherInlineDisplay } from 'apps/fleet-planner/components/atoms/DispatcherInlineDisplay'
import { ExternalTooltipMessage } from 'apps/fleet-planner/components/load-details/ExternalTooltipMessage'
import { useShipmentApi } from 'apps/fleet-planner/hooks/useShipmentApi'
import { IDispatcher } from 'apps/fleet-planner/types'
import * as Types from 'apps/fleet-planner/types'
import { dispatchersListSelector } from 'apps/fleet-planner/utils/selectors'
import { Button, Card, InlineEditContainer } from 'transfix-ui'

interface IDispatcherInlineEditProps {
  dispatcher?: IDispatcher
  currentEditField?: string
  isExternalDispatcher?: boolean
  onEditModeRequested: (name?: string) => void
}

const NAME = 'dispatcher'

export const DispatcherInlineEdit = memo<IDispatcherInlineEditProps>(
  ({ currentEditField, dispatcher: dispatcherProp, isExternalDispatcher, onEditModeRequested }) => {
    const dispatchers = useSelector(dispatchersListSelector)
    const { updateShipmentDispatcher } = useShipmentApi()

    const [localChanges, setLocalChanges] = useState<Pick<Types.ILoad, 'dispatcher'>>({
      dispatcher: dispatcherProp as Types.IDispatcher,
    })

    // Update state when receiving new props
    useEffect(() => {
      setLocalChanges(prev => ({ ...prev, dispatcher: dispatcherProp }))
    }, [dispatcherProp])

    const resetLocalChanges = () => {
      setLocalChanges({ dispatcher: dispatcherProp })
    }

    const changeHandler = (dispatcherId?: string) => {
      const matchingDispatcher = dispatchers.find(disp => disp.id.toString() === dispatcherId)
      setLocalChanges({
        dispatcher: matchingDispatcher,
      })
    }

    const onSave = () => {
      onEditModeRequested('')

      // Update shipment
      if (localChanges.dispatcher?.id) {
        updateShipmentDispatcher(localChanges.dispatcher.id)
      }
    }

    const onCancel = () => {
      resetLocalChanges()
      onEditModeRequested('')
    }

    const isEditMode = currentEditField === NAME

    const editModeRequestedHandler = (name?: string) => {
      if (!name) {
        resetLocalChanges()
      }
      onEditModeRequested(name)
    }

    return (
      <>
        <InlineEditContainer
          className='relative mt4'
          name={NAME}
          editModeEnabled={isEditMode}
          dataTest='dispatcher-inline-edit'
          editMode={
            <Card className='pa4'>
              <DispatcherSelect
                label='Dispatcher'
                dispatcherId={localChanges.dispatcher?.id || ''}
                onChange={changeHandler}
                large
                required
              />
              <div className='tr'>
                <div className='mt4'>
                  <Button color='tertiary' size='large' onClick={onCancel}>
                    Cancel
                  </Button>
                  <Button
                    color='primary'
                    size='large'
                    onClick={onSave}
                    className='ml2'
                    disabled={!localChanges.dispatcher?.id}>
                    Save
                  </Button>
                </div>
              </div>
            </Card>
          }
          onEditModeRequested={editModeRequestedHandler}>
          <DispatcherInlineDisplay value={dispatcherProp} />
        </InlineEditContainer>
        {isExternalDispatcher && (
          <ExternalTooltipMessage
            tooltipMessage='This dispatcher info is what Transfix had on file when this load was imported into Transfix Carrier App. You can assign this load to one of your dispatchers.'
            message={`"${dispatcherProp?.fullName}" at Transfix (not a user)`}
            id='dispatcher-marketplace'
          />
        )}
      </>
    )
  }
)
